import { useState, useEffect, useRef, useCallback } from 'react';

interface UseVoiceInputOptions {
  onTranscriptChange?: (transcript: string) => void;
  onFinalTranscript?: (transcript: string) => void;
  onError?: (error: string) => void;
  silenceTimeout?: number; // milliseconds
}

export interface UseVoiceInputReturn {
  isListening: boolean;
  transcript: string;
  isSupported: boolean;
  permissionDenied: boolean;
  startListening: () => Promise<void>;
  stopListening: () => void;
  resetTranscript: () => void;
}

// Check if Web Speech API is supported
const isSpeechRecognitionSupported = (): boolean => {
  return 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
};

export function useVoiceInput(options: UseVoiceInputOptions = {}): UseVoiceInputReturn {
  const {
    onTranscriptChange,
    onFinalTranscript,
    onError,
    silenceTimeout = 3000,
  } = options;

  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isSupported] = useState(isSpeechRecognitionSupported());
  const [permissionDenied, setPermissionDenied] = useState(false);

  const recognitionRef = useRef<any>(null);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const finalTranscriptRef = useRef('');

  // Initialize speech recognition
  useEffect(() => {
    if (!isSupported) return;

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      setIsListening(true);
      setPermissionDenied(false);
      finalTranscriptRef.current = '';
    };

    recognition.onresult = (event: any) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcriptPiece = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcriptPiece + ' ';
        } else {
          interimTranscript += transcriptPiece;
        }
      }

      // Accumulate final transcript
      if (finalTranscript) {
        finalTranscriptRef.current += finalTranscript;
      }

      // Combine accumulated final transcript with current interim
      const fullTranscript = (finalTranscriptRef.current + interimTranscript).trim();
      setTranscript(fullTranscript);
      onTranscriptChange?.(fullTranscript);

      // Reset silence timer on new speech
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }

      // Start silence timer
      silenceTimerRef.current = setTimeout(() => {
        if (recognitionRef.current && isListening) {
          stopListening();
        }
      }, silenceTimeout);
    };

    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);

      if (event.error === 'not-allowed' || event.error === 'permission-denied') {
        setPermissionDenied(true);
        onError?.('Microphone permission denied. Please enable microphone access in your browser settings.');
      } else if (event.error === 'no-speech') {
        // No speech detected, this is normal
        onError?.('No speech detected. Please try again.');
      } else if (event.error === 'network') {
        onError?.('Network error. Please check your internet connection.');
      } else {
        onError?.(`Speech recognition error: ${event.error}`);
      }

      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
      
      // Clear silence timer
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }

      // Call final transcript callback
      if (finalTranscriptRef.current.trim()) {
        onFinalTranscript?.(finalTranscriptRef.current.trim());
      }
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }
    };
  }, [isSupported, onTranscriptChange, onFinalTranscript, onError, silenceTimeout, isListening]);

  const startListening = useCallback(async (): Promise<void> => {
    if (!isSupported) {
      onError?.('Speech recognition is not supported in this browser.');
      return;
    }

    // Check if we're on HTTPS or localhost
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      onError?.('Speech recognition requires HTTPS. Please use a secure connection.');
      return;
    }

    try {
      // Request microphone permission
      await navigator.mediaDevices.getUserMedia({ audio: true });
      
      if (recognitionRef.current && !isListening) {
        setTranscript('');
        finalTranscriptRef.current = '';
        recognitionRef.current.start();
      }
    } catch (error) {
      console.error('Microphone permission error:', error);
      setPermissionDenied(true);
      onError?.('Microphone permission denied. Please enable microphone access in your browser settings.');
    }
  }, [isSupported, isListening, onError]);

  const stopListening = useCallback((): void => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
    }
    
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, [isListening]);

  const resetTranscript = useCallback((): void => {
    setTranscript('');
    finalTranscriptRef.current = '';
  }, []);

  return {
    isListening,
    transcript,
    isSupported,
    permissionDenied,
    startListening,
    stopListening,
    resetTranscript,
  };
}
